import * as Sentry from "@sentry/react";
import { ApiError } from "@/services/api";

/**
 * Nature réelle d'une erreur, telle qu'on doit la présenter à l'utilisateur.
 *
 * Historiquement toutes les causes ci-dessous s'affichaient avec le même texte
 * (« Erreur serveur. Veuillez contacter l'administrateur. »), y compris quand la requête
 * avait réussi mais renvoyé une charge vide. On les distingue désormais pour que le
 * message dise la vérité et propose la bonne action.
 */
export type ErrorKind =
  /** Pas de réseau, ou le serveur est injoignable. */
  | "offline"
  /** Session expirée / non authentifié (401). */
  | "session"
  /** Le serveur a répondu mais a échoué (5xx). */
  | "server"
  /** Requête refusée côté applicatif (4xx : droits, validation, introuvable). */
  | "client"
  /** Tout le reste : crash de rendu, erreur non identifiée. */
  | "unknown";

/**
 * Signatures connues d'un chunk introuvable après déploiement (noms de fichiers hashés qui ont
 * changé), formulées différemment selon le moteur — Safari, Chrome/Edge, Firefox, plus le message
 * générique de Vite lui-même.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /is not a valid javascript mime type|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|unable to preload css/i;

/**
 * Échec d'un `<link rel=modulepreload>` — le préchargement en arrière-plan d'un chunk que le
 * navigateur n'a pas encore besoin d'exécuter, distinct d'un `import()` dynamique réellement
 * bloquant (`CHUNK_LOAD_ERROR_PATTERN` ci-dessus, qui déclenche un rechargement dans
 * `vite:preloadError`).
 *
 * Le message ne nomme jamais le fichier ni la cause — Safari (« Load failed ») et Chrome
 * (« Failed to fetch ») renvoient le texte générique de leur propre échec de `fetch`, que le
 * polyfill de préchargement du bundle Vite laisse remonter tel quel jusqu'à un rejet non
 * intercepté (SUCCESCHOOL-FRONTEND-V2-146746893). Une préchargement ratée n'empêche pas l'`import()`
 * qui suivra de refonctionner normalement — d'où pas de rechargement forcé ici, seulement un
 * regroupement Sentry pour ne pas la confondre avec un vrai bug de rendu.
 */
const PRELOAD_FETCH_ERROR_PATTERN = /^(load failed|failed to fetch)$/i;

/** Repère un échec de `modulepreload` : un `TypeError` dont le message est le texte générique du navigateur pour un `fetch` en échec, sans plus de contexte. */
export function isPreloadFetchError(error: unknown): error is TypeError {
  return error instanceof TypeError && PRELOAD_FETCH_ERROR_PATTERN.test(error.message.trim());
}

/**
 * Isole les échecs de chunk post-déploiement des vrais bugs de rendu.
 *
 * Déjà auto-résolus par un rechargement, ils ne demandent aucune action de l'équipe : une empreinte
 * dédiée les regroupe en une seule issue plutôt que de les disperser par message (un par moteur), et
 * le niveau « warning » les distingue d'un coup d'œil des erreurs de rendu, qui restent en « error ».
 *
 * Vit ici, et non dans l'une des deux frontières d'erreur, parce que **les deux** en ont besoin : un
 * `lazy()` de route échoue dans l'`errorElement` du routeur, un `lazy()` de composant — le lecteur
 * PDF, le lecteur vidéo — dans l'`ErrorBoundary` React le plus proche. La même panne était classée
 * en « warning » d'un côté et en « error » non taguée de l'autre (SUCCESCHOOL-FRONTEND-V2-1D).
 */
/**
 * Compose le message d'échec d'une action, en gardant celui des deux — serveur ou contexte — qui
 * en dit le plus.
 *
 * Sur un 4xx, le serveur nomme précisément ce qui cloche (« Fichier trop volumineux », « Certaines
 * informations sont invalides ») : son message est le plus utile, on le garde tel quel.
 *
 * Sur un 5xx ou une coupure, il ne peut nommer qu'une cause générique — `defaultMessageForStatus`
 * la fabrique à partir du seul code de statut, sans rien savoir de ce que l'utilisateur tentait.
 * « Le serveur a rencontré un problème » est alors exact mais inexploitable : l'élève ignore si son
 * devoir est parti et s'il doit recommencer, et la lecture la plus naturelle — « ce n'est pas mon
 * souci » — est justement la mauvaise (SUCCESCHOOL-FRONTEND-V2-4D). On annonce donc d'abord le sort
 * de l'action, la cause passant en second plan.
 *
 * Le `notify.error(err.message || "…")` répandu dans les hooks ne pouvait pas faire ce choix : le
 * repli contextuel n'est atteint que si le message est vide, ce qui n'arrive jamais.
 */
export function describeActionFailure(
  error: unknown,
  actionFailed: string,
): { message: string; description?: string } {
  const serverMessage = error instanceof Error ? error.message : "";
  if (!serverMessage) return { message: actionFailed };

  // `status === 0` : `NetworkError`, la requête n'a atteint personne (cf. sa définition).
  const isGeneric = error instanceof ApiError && (error.status >= 500 || error.status === 0);
  return isGeneric
    ? { message: actionFailed, description: serverMessage }
    : { message: serverMessage };
}

export function isChunkLoadError(error: unknown): error is Error {
  return error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message);
}

export function classifyError(error: unknown): ErrorKind {
  // Une coupure réseau côté navigateur prime : inutile de parler de « serveur »
  // à quelqu'un dont le wifi vient de tomber.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";

  if (error instanceof ApiError) {
    // status 0 = la requête n'a jamais atteint le serveur (cf. NetworkError dans api.ts).
    if (error.status === 0) return "offline";
    if (error.status === 401) return "session";
    if (error.status >= 500) return "server";
    if (error.status >= 400) return "client";
    return "unknown";
  }

  // `fetch` rejette avec un TypeError quand la connexion échoue.
  if (error instanceof TypeError) return "offline";

  return "unknown";
}

/**
 * Message à afficher quand une action échoue.
 *
 * Le message d'une erreur n'est présentable que si quelqu'un l'a écrit pour être lu. C'est le cas
 * d'un refus applicatif (4xx) : le backend y explique en français ce qui bloque, et le remplacer par
 * une phrase générique ferait perdre la seule information utile. Ça ne l'est pas d'une panne (5xx,
 * réseau), dont le message décrit une mécanique interne.
 *
 * Sans ce tri, une enseignante à qui LiveKit répondait 404 lisait « LiveKit RoomService
 * .ListParticipants failed (404): requested room does not exist » dans une notification française,
 * au milieu de son cours (SUCCESCHOOL-FRONTEND-V2-3P).
 *
 * **`reason_code` prime sur le statut.** Déduire d'une classe de statut qu'un message est affichable
 * reste une inférence, et elle se trompe dans les deux sens : un 5xx peut porter un texte rédigé
 * pour l'enseignante (`livekit_action_refused` — « Aucun micro actif chez l'enseignant. Activez le
 * micro puis relancez l'enregistrement. »), pendant qu'une exception non interceptée d'une autre
 * route n'en porte aucun. Le marqueur, lui, est une intention déclarée : le backend affirme avoir
 * écrit ce message pour être lu.
 *
 * Contrat, convenu avec le backend le 23/08 : `reason_code` présent → message affiché tel quel, quel
 * que soit le statut ; absent → le tri par statut ci-dessus, inchangé. Aucun assouplissement par
 * classe de statut n'est donc nécessaire, et une nouvelle valeur ne demande aucun déploiement front.
 */
export function actionErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.reasonCode && error.message) return error.message;
  if (classifyError(error) !== "client") return fallback;
  return error instanceof ApiError && error.message ? error.message : fallback;
}

/**
 * Référence courte affichée à l'utilisateur et cherchable telle quelle dans Sentry.
 *
 * Objectif métier : transformer un signalement « j'ai eu une erreur vers 8h07 » en un
 * identifiant qui pointe directement l'événement (stack, requête, replay de session).
 *
 * Renvoie `undefined` si Sentry n'est pas actif (dev, ou DSN absent en prod) : mieux vaut
 * n'afficher aucune référence qu'une référence introuvable par le support.
 */
export function errorReference(error: unknown): string | undefined {
  if (!Sentry.getClient()) return undefined;
  const eventId = error instanceof ApiError ? error.eventId : undefined;
  return formatReference(eventId);
}

/** Met en forme un event id Sentry (32 hex) en référence lisible : `A1B2C3D4`. */
export function formatReference(eventId: string | undefined): string | undefined {
  if (!eventId) return undefined;
  return eventId.slice(-8).toUpperCase();
}
